<script setup>
import { computed } from 'vue';
import Codemirror from 'codemirror-editor-vue3';

import 'codemirror/mode/javascript/javascript.js';
import 'codemirror/mode/htmlmixed/htmlmixed.js';
import 'codemirror/mode/css/css.js';
import 'codemirror/mode/xml/xml.js';
import 'codemirror/mode/markdown/markdown.js';

import 'codemirror/theme/material.css';
import 'codemirror/theme/dracula.css';
import 'codemirror/theme/monokai.css';
import 'codemirror/theme/solarized.css';

const emit = defineEmits(['update:modelValue', 'update:focused']);

const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    editable: {
        type: Boolean,
        default: true
    },
    mode: {
        type: String,
        default: 'edit'
    },
    label: {
        type: String,
        default: ''
    },
    errorMessages: {
        type: [String, Array],
        default: null
    },
    align: {
        type: String,
        default: 'left'
    },
    disabled: {
        type: Boolean,
        default: false
    },
    args: {
        type: Object,
        default: () => ({})
    }
});

const model = computed({
    get: () => props.modelValue || '',
    set: (value) => {
        emit('update:modelValue', value);
    }
});

const language = computed(() => {
    const validLanguages = [
        'javascript',
        'htmlmixed',
        'css',
        'xml',
        'markdown'
    ];
    return validLanguages.includes(props.args?.language)
        ? props.args.language
        : 'javascript';
});

const theme = computed(() => {
    const validThemes = [
        'default',
        'material',
        'dracula',
        'monokai',
        'solarized'
    ];
    return validThemes.includes(props.args?.theme)
        ? props.args.theme
        : 'default';
});

const cmOptions = computed(() => {
    return {
        mode: language.value,
        theme: theme.value,
        lineNumbers: true,
        lineWrapping: true,
        autoCloseBrackets: true,
        matchBrackets: true,
        indentUnit: 4,
        tabSize: 4,
        readOnly: !props.editable || props.mode !== 'edit' || props.disabled,
        placeholder: props.label || 'Escribe tu código aquí...',
        ...props.args?.options
    };
});

const height = computed(() => {
    return props.args?.height || '300px';
});

const style = computed(() => {
    const baseStyle = {
        height: height.value,
        width: '100%'
    };

    if (props.args?.style) {
        if (typeof props.args.style === 'string') {
            const styleObj = {};
            props.args.style.split(';').forEach((rule) => {
                const [property, value] = rule.split(':').map((s) => s.trim());
                if (property && value) {
                    const camelProperty = property.replace(/-([a-z])/g, (g) =>
                        g[1].toUpperCase()
                    );
                    styleObj[camelProperty] = value;
                }
            });
            return { ...baseStyle, ...styleObj };
        }
        return { ...baseStyle, ...props.args.style };
    }

    return baseStyle;
});

const handleChange = (value) => {
    model.value = value;
};

const handleFocus = () => {
    emit('update:focused', true);
};

const handleBlur = () => {
    emit('update:focused', false);
};

const insertTextAtCursor = (text) => {
    model.value = model.value + text;
};

defineExpose({
    insertTextAtCursor
});
</script>

<template>
    <div :style="style">
        <Codemirror
            v-model:value="model"
            :options="cmOptions"
            :height="height"
            border
            :placeholder="label"
            @change="handleChange"
            @focus="handleFocus"
            @blur="handleBlur"
        />
    </div>
</template>

<style lang="scss" scoped>
/* Estilos personalizados para el editor */
:deep(.CodeMirror) {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 14px;
    line-height: 1.5;
    border-radius: 4px;
}

:deep(.CodeMirror-focused) {
    border-color: #1976d2;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

/* Alineación de texto */
.text-right :deep(.CodeMirror) {
    text-align: right;
}
.text-left :deep(.CodeMirror) {
    text-align: left;
}
.text-center :deep(.CodeMirror) {
    text-align: center;
}
</style>
